import type { EventqButtonVariant } from '../../primitives/button/button';
import type { EventqInfoCardMeta, EventqInfoCardNote, EventqInfoCardTone } from '../info-card/info-card';
import type { EventqSelectOption } from '../select/select.types';
import type { EventqActionableListAppearance } from '../actionable-list/actionable-list.types';

export type EventqActionFormFieldWidth = 'flex' | number;

export interface EventqActionFormSelectField {
  type: 'select';
  key: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  /** Force red invalid chrome on the select even when the value is present. */
  invalid?: boolean;
  /** Filled trigger treatment. `danger` is Skip outcome (red-950). */
  tone?: 'default' | 'danger';
  placeholder?: string;
  options: readonly EventqSelectOption[];
  width?: EventqActionFormFieldWidth;
}

export interface EventqActionFormTextField {
  type: 'text' | 'textarea';
  key: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Non-editable, but still required when `required` is set.
   * `disabled` drops the field from `areRequiredFieldsSatisfied`.
   */
  readOnly?: boolean;
  placeholder?: string;
  rows?: number;
  width?: EventqActionFormFieldWidth;
}

export interface EventqActionFormDatetimeField {
  type: 'datetime';
  key: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  width?: EventqActionFormFieldWidth;
}

export type EventqActionFormField =
  | EventqActionFormSelectField
  | EventqActionFormTextField
  | EventqActionFormDatetimeField;

export interface EventqActionFormRow {
  type: 'row';
  fields: readonly EventqActionFormField[];
}

export interface EventqActionFormDropzone {
  type: 'dropzone';
  key: string;
  label: string;
  hint?: string;
  accept?: string;
  multiple?: boolean;
}

/**
 * Embeds `eventq-actionable-list`. Rows live in `values[itemsKey]`;
 * search text in `values[searchKey]` (emit-only; parent filters rows).
 */
export interface EventqActionFormActionableList {
  type: 'actionableList';
  /** Section id used when forwarding activate/info events. */
  key: string;
  appearance?: EventqActionableListAppearance;
  /** `values` key for `EventqActionableListItem[]`. */
  itemsKey: string;
  /** `values` key for the controlled search string. */
  searchKey?: string;
  showSearch?: boolean;
  activateLabel?: string;
  /** Max height of the row viewport; short lists shrink to their rows. */
  listHeight?: string | null;
  listAriaLabel?: string;
  loading?: boolean;
  skeletonCount?: number | null;
  disabled?: boolean;
}

export interface EventqActionFormInfoCard {
  type: 'infoCard';
  tone?: EventqInfoCardTone;
  category?: string;
  title: string;
  meta?: readonly EventqInfoCardMeta[];
  note?: EventqInfoCardNote | null;
  notes?: readonly EventqInfoCardNote[];
}

export interface EventqActionFormAction {
  id: string;
  label: string;
  variant?: EventqButtonVariant;
  /** When true, validates required fields in the enclosing group before emitting. */
  validates?: boolean;
}

export interface EventqActionFormActions {
  type: 'actions';
  actions: readonly EventqActionFormAction[];
}

export type EventqActionFormSection =
  | EventqActionFormRow
  | EventqActionFormDropzone
  | EventqActionFormActionableList
  | EventqActionFormInfoCard
  | EventqActionFormActions
  | EventqActionFormGroup;

export interface EventqActionFormGroup {
  type: 'group';
  id: string;
  sections: readonly EventqActionFormSection[];
}

export interface EventqActionFormConfig {
  sections: readonly EventqActionFormSection[];
}

export type EventqActionFormValues = Record<string, unknown>;

export interface EventqActionFormActionEvent {
  id: string;
  groupId?: string;
}

export interface EventqActionFormFilesEvent {
  key: string;
  files: File[];
  groupId?: string;
}

export interface EventqActionFormListActivateEvent {
  id: string;
  key: string;
  groupId?: string;
  optionId?: string;
}

export interface EventqActionFormListInfoEvent {
  id: string;
  key: string;
  groupId?: string;
}

export function isEventqActionFormFieldSection(
  section: EventqActionFormSection,
): section is EventqActionFormRow {
  return section.type === 'row';
}

export function collectRequiredFieldKeys(
  sections: readonly EventqActionFormSection[],
): string[] {
  const keys: string[] = [];

  for (const section of sections) {
    if (section.type === 'row') {
      for (const field of section.fields) {
        if (field.required && !field.disabled) {
          keys.push(field.key);
        }
      }
    } else if (section.type === 'group') {
      keys.push(...collectRequiredFieldKeys(section.sections));
    }
  }

  return keys;
}

export function isEventqActionFormValuePresent(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

export function areRequiredFieldsSatisfied(
  sections: readonly EventqActionFormSection[],
  values: EventqActionFormValues,
): boolean {
  return collectRequiredFieldKeys(sections).every((key) =>
    isEventqActionFormValuePresent(values[key]),
  );
}
